import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLayerStore } from '../stores/layerStore';
import { documentsApi } from '../services/api';
import { queryKeys } from '../queries/queryKeys';
import { RedlineSegmentsResponse } from '../services/api/negotiationApi';

// Module-level dedup to prevent multiple hook instances probing the same doc
const probedDocuments = new Set<string>();
const inFlightProbes = new Map<string, Promise<void>>();

/**
 * Decide whether the tracked-changes layer should be registered from a
 * redline-segments probe response. Plan 48 / #854: a `too_large` response
 * carries no segments but DOES mean the doc has tracked changes (the inline
 * diff was just too big to render) — so the layer must register, otherwise the
 * slow path would mislabel a large redlined doc as "no tracked changes".
 */
export function probeShouldRegisterLayer(
  res: { data?: RedlineSegmentsResponse } | null | undefined
): boolean {
  const data = res?.data;
  if (data?.too_large) return true;
  return Object.keys(data?.segments_by_clause ?? {}).length > 0;
}

export function clearTrackedChangesProbeCache(documentId?: string) {
  if (documentId) {
    probedDocuments.delete(documentId);
    inFlightProbes.delete(documentId);
  } else {
    probedDocuments.clear();
    inFlightProbes.clear();
  }
}

/**
 * Auto-detects whether a document has tracked changes and registers
 * the 'trackedChanges' layer.
 *
 * Fast path: If has_tracked_changes boolean is available from the documents
 * list API, the layer is registered instantly without an API call.
 *
 * Slow path (fallback): Fetches redline segments from the API.
 */
export function useTrackedChangesLayerDetection(
  documentId: string | null | undefined,
  hasTrackedChanges?: boolean
) {
  const addAvailableLayer = useLayerStore((s) => s.addAvailableLayer);
  const removeAvailableLayer = useLayerStore((s) => s.removeAvailableLayer);
  const queryClient = useQueryClient();

  // Fast path: use has_tracked_changes from document metadata (no API call)
  useEffect(() => {
    if (!documentId || hasTrackedChanges === undefined) return;

    if (hasTrackedChanges) {
      addAvailableLayer(documentId, 'trackedChanges');
      probedDocuments.add(documentId);
    } else {
      removeAvailableLayer(documentId, 'trackedChanges');
      probedDocuments.add(documentId);
    }
  }, [documentId, hasTrackedChanges, addAvailableLayer, removeAvailableLayer]);

  // Slow path: fetch redline segments if metadata not available
  // Also pre-populates the query cache so the card view renders instantly
  useEffect(() => {
    if (!documentId) return;
    if (probedDocuments.has(documentId)) return;
    if (inFlightProbes.has(documentId)) return;
    // If metadata provided, fast path handles it
    if (hasTrackedChanges !== undefined) return;

    let cancelled = false;

    const probe = documentsApi
      .getRedlineSegments(documentId)
      .then((res) => {
        if (cancelled) return;
        const hasSegments =
          Object.keys(res?.data?.segments_by_clause ?? {}).length > 0;
        if (probeShouldRegisterLayer(res)) {
          // Only seed the query cache when there are real segments. A
          // too_large response has none, but the layer must still register
          // (#854) so the doc isn't mislabelled "no tracked changes".
          if (hasSegments) {
            queryClient.setQueryData(queryKeys.documents.redlineSegments(documentId), res);
          }
          addAvailableLayer(documentId, 'trackedChanges');
        } else {
          removeAvailableLayer(documentId, 'trackedChanges');
        }
        probedDocuments.add(documentId);
      })
      .catch(() => {
        if (!cancelled) {
          probedDocuments.add(documentId);
        }
      });

    inFlightProbes.set(documentId, probe);
    probe.finally(() => inFlightProbes.delete(documentId));

    return () => {
      cancelled = true;
    };
  }, [documentId, hasTrackedChanges, addAvailableLayer, removeAvailableLayer, queryClient]);
}
